class TenantConfigService{
    #env;
    #baseEndPoint;
    #headers;
    #hostName;
    #orgId;
    #tenantId;
    #debugMode;
    #isLive;

    constructor( ){
        this.#env = "";
        this.#baseEndPoint = "";
        this.#headers = {};
        this.#hostName = "";
        this.#orgId = 0;
        this.#tenantId = 0;
        this.#debugMode = false;
        this.#isLive = import.meta.env.instance === "LIVE";
        this.#init();
    }

    /**
     * Retrieves the tenant auth0 configuration for the current hostname.
     * Makes a GET request to the public tenants endpoint to fetch auth config data.
     *
     * @async
     * @returns {Promise<Object>} Promise that resolves to an object containing:
     *   - authConfig: The tenant authentication configuration data
     *   - env: The current environment
     * @returns {Promise<Array>} Returns error array if request fails (via handleErrorReturnArray)
     * @throws {Error} Throws error if tenant auth config cannot be retrieved
     */
    async getTenantAuthConfig(){
        const ENDPOINT = `${ this.#baseEndPoint }/public/tenants?domain=${ this.#hostName }`;
        const errMsg = "Could not GET tenant auth config";

        try {
            const TENANT_AUTH = await fetch( ENDPOINT, { headers: this.#headers } )
                .then( data => {
                    if ( data.ok ){
                        return data.json();
                    }
                } );

            if ( !TENANT_AUTH ){
                throw new Error( errMsg );
            }

            return { authConfig: TENANT_AUTH, env: this.#env };
        } catch ( err ){
            return this.handleErrorReturnArray( err );
        }
    }

    /**
     * Retrieves the tenant configuration property tree from the API endpoint.
     *
     * @async
     * @method getTenantConfig
     * @returns {Promise<Object|Array>} The tenant property tree object on success, or an error array on failure
     * @throws {Error} Throws an error if the tenant property tree is not found in the response
     */
    async getTenantConfig(){
        const ENDPOINT = `${ this.#baseEndPoint }/tenants/${ this.#tenantId }/orgs/${ this.#orgId }/properties/tree`;
        const errMsg = "Could not GET tenant property tree.";

        try {
            const TENANT_TREE = await fetch( ENDPOINT, { headers: this.#headers } )
                .then( res => res.json() );

            if ( !TENANT_TREE?.tenantPropertyTree ){
                throw new Error( errMsg );
            }

            return TENANT_TREE.tenantPropertyTree;
        } catch ( err ){
            return this.handleErrorReturnArray( err );
        }
    }

    /**
     * Retrieves the tenant persona mapping list for the current tenant and organization.
     *
     * @async
     * @function getTenantPersona
     * @returns {Promise<Array>} A promise that resolves to an array of persona mapping objects,
     *                          or an empty array if an error occurs or no persona mappings are found.
     * @throws {Error} Throws an error if the API request fails or the response structure is invalid.
     */
    async getTenantPersona(){
        const ENDPOINT = `${ this.#baseEndPoint }/tenants/${ this.#tenantId }/orgs/${ this.#orgId }/personas`;
        const errMsg = "Could not GET tenant persona mapping";

        try {
            const TENANT_PERSONAS = await fetch( ENDPOINT, { headers: this.#headers } )
                .then( res => res.json() );

            if ( !TENANT_PERSONAS?._embedded?.personaMappingList ){
                throw new Error( errMsg );
            }

            return TENANT_PERSONAS._embedded.personaMappingList;
        } catch ( err ){
            return this.handleErrorReturnArray( err );
        }
    }

    /**
     *
     * @param {String} err
     * @returns {Array} empty array
     */
    handleErrorReturnArray( err ){
        console.error( err );

        return [];
    }

    /**
     * Sets the HTTP headers for API requests including authorization and tenant identification.
     * @param {string} bearerToken - The bearer token for API authentication
     * @returns {void}
     */
    setHeaders( bearerToken ){
        this.#headers = {
            Accept: "application/json",
            Authorization: `Bearer ${ bearerToken }`,
            "X-TENANT-ID": String( this.#tenantId )
        };
    }

    /**
     * Sets the organization ID and tenant ID for the service instance.
     * @param {Object} params - The parameters object
     * @param {number} [params.orgId=0] - The organization ID to set
     * @param {number} [params.tenantId=0] - The tenant ID to set
     */
    setTenantIds( { orgId = 0, tenantId = 0 } ){
        this.#orgId = orgId;
        this.#tenantId = tenantId;
    }

    /**
     * Initializes the TenantConfigService by setting up the hostname, environment, and endpoint configuration.
     * This method is called internally during service instantiation to configure the service with the necessary parameters.
     * @private
     */
    #init(){
        this.#setHostName();
        this.#setEnv();
        this.#setEndpoint();
    }

    /**
     * Sets the base endpoint URL for the tenant configuration service.
     * Constructs the URL using the environment value in lowercase format.
     * The endpoint follows the pattern: https://tsunami-configservice-{env}.azurewebsites.net/api
     *
     * @private
     * @returns {void}
     */
    #setEndpoint(){
        let envString = "";

        if ( this.#env !== "Prod" ){
            envString = `-${ this.#env?.toLowerCase() }`;
        }

        this.#baseEndPoint = `https://tsunami-configservice${ envString }.azurewebsites.net/api`;
    }

    /**
     * Determines the environment type based on the provided host string.
     *
     * @param {string} host - The host string to evaluate (e.g., a URL or hostname).
     * @returns {string} The environment type: "dev", "qa", "stage", or "prod".
     */
    #setEnv(){
        if ( !this.#hostName ){
            this.#setHostName();
        }

        if ( !this.#isLive ){
            if ( this.#debugMode ){
                // eslint-disable-next-line no-console
                console.info(
                    `%c******  DEV MODE: CURRENT LOCAL ENV : ${ import.meta.env.localEnv }  ****** `,
                    "color: white; background: linear-gradient(to right, rgb(7 152 240), rgb(2 50 79))"
                );
            }

            this.#env = import.meta.env.localEnv;

            return;
        }

        let ENV = "Prod";

        switch ( true ){
            case /-dev/i.test( this.#hostName ):
                ENV = "Dev";
                break;
            case /-qa/i.test( this.#hostName ):
                ENV = "Qa";
                break;
            case /-stage/i.test( this.#hostName ):
                ENV = "Stage";
                break;
            default:
                ENV = "Prod";
                break;
        }

        this.#env = ENV;
    }

    /**
     * Sets the hostname for the configuration.
     * In "local" (non-live) environments, attempts to extract hostname from SITE_URL environment variable
     * or falls back to current window location. Logs debug information if debug mode is enabled.
     *
     * @private
     * @param {string} [host=""] - The hostname to set. If empty, will be determined automatically
     * @returns {void}
     */
    #setHostName(){
        if ( !this.#isLive ){
            const host = new URL( import.meta.env.SITE_URL || window.location.href )?.hostname || "";

            if ( this.#debugMode ){
                // eslint-disable-next-line no-console
                console.info(
                    `%c******  DEV MODE: SITE NAME - ${ host }  ******`,
                    "color: white; background: linear-gradient(to right, rgb(7 152 240), rgb(2 50 79))"
                );
            }

            this.#hostName = host;

            return;
        }

        this.#hostName = window.location.hostname;
    }

}

export default new TenantConfigService();